// Canonical production origin. Every canonical / OG URL is built from this,
// so changing the domain is a one-line edit.
export const SITE_URL = 'https://ecranify.com'

// Default social share image used when a page does not provide its own.
export const DEFAULT_OG_IMAGE = '/og/ecranify-og.svg'

// The hub page listing every guide + comparison. Linked from the footer, which
// only carries a short hand-picked subset.
export const GUIDES_INDEX_PATH = '/guides'

// Absolute URL helper for canonical / OG tags.
export const absoluteUrl = (path: string): string => {
  if (path.startsWith('http')) return path
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

// Central registry of the prerendered marketing pages. Drives cross-linking
// between pages, the `/guides` index page, and doubles as the source of truth
// referenced by fe/scripts/prerender.mjs + public/sitemap.xml (keep in sync).
// The footer "Guides" column is a separate, deliberately short manual list.
export interface MarketingPage {
  readonly path: string
  readonly label: string
  // 'tool' entries (the /tools hub) are excluded from the /guides index page's
  // guide/comparison filters (GuidesIndexPage.tsx) but still appear in
  // relatedMarketingPages' cross-links on every guide/comparison article.
  readonly kind: 'comparison' | 'guide' | 'tool'
  // One-line summary, shown on the /guides index page.
  readonly summary: string
}

export const MARKETING_PAGES: readonly MarketingPage[] = [
  {
    path: '/tools',
    label: 'Free tools',
    kind: 'tool',
    summary: 'Icon generator, QR code generator, image converter and video-to-GIF — all free, all in your browser.',
  },
  {
    path: '/alternatives/appmockup',
    label: 'Ecranify vs AppMockUp',
    kind: 'comparison',
    summary: 'How the two tools compare on 3D control, device coverage, export sizes and pricing.',
  },
  {
    path: '/vs/rotato',
    label: 'Ecranify vs Rotato',
    kind: 'comparison',
    summary: 'Browser-based 3D mockups versus a desktop app built around animation and video.',
  },
  {
    path: '/alternatives/previewed',
    label: 'Ecranify vs Previewed',
    kind: 'comparison',
    summary: 'Template-driven screenshot generation compared with a real 3D scene you control.',
  },
  {
    path: '/vs/screenshots-pro',
    label: 'Ecranify vs Screenshots Pro',
    kind: 'comparison',
    summary: 'Animated previews and localization versus 3D camera, lighting and carousel export.',
  },
  {
    path: '/alternatives/screenhance',
    label: 'Ecranify vs Screenhance',
    kind: 'comparison',
    summary: 'An all-in-one mockup, motion and video studio compared with a focused 3D screenshot editor.',
  },
  {
    path: '/alternatives/applaunchpad',
    label: 'Ecranify vs AppLaunchpad',
    kind: 'comparison',
    summary: 'A template library with preset angles and AI translation compared with a free-form 3D screenshot scene.',
  },
  {
    path: '/alternatives/appscreens',
    label: 'Ecranify vs AppScreens',
    kind: 'comparison',
    summary: 'A template, translation and store-upload tool compared with a free-form 3D screenshot scene.',
  },
  {
    path: '/alternatives/screenfast',
    label: 'Ecranify vs ScreenFast',
    kind: 'comparison',
    summary: 'An AI generator of iPhone screenshot designs compared with a hands-on 3D editor for every store size.',
  },
  {
    path: '/alternatives/butterkit',
    label: 'Ecranify vs ButterKit',
    kind: 'comparison',
    summary: 'A native Mac app for App Store screenshots compared with a browser-based 3D editor that runs on any OS.',
  },
  {
    path: '/alternatives/appdrift',
    label: 'Ecranify vs AppDrift',
    kind: 'comparison',
    summary: 'An ASO workspace with a template screenshot editor compared with a hands-on 3D screenshot editor.',
  },
  {
    path: '/alternatives/appscreenshotstudio',
    label: 'Ecranify vs AppScreenshotStudio',
    kind: 'comparison',
    summary: 'An AI chat screenshot generator with preset device views compared with a hands-on 3D editor.',
  },
  {
    path: '/alternatives/screenshotwhale',
    label: 'Ecranify vs ScreenshotWhale',
    kind: 'comparison',
    summary: 'An AI generator that designs and translates screenshot sets compared with a hands-on 3D editor.',
  },
  {
    path: '/guides/app-store-screenshot-sizes',
    label: 'App Store & Play screenshot sizes',
    kind: 'guide',
    summary:
      'Required and optional screenshot sizes for both stores, incl. the newly required 6.3″ iPhone set and iPhone Duo.',
  },
  {
    path: '/guides/google-play-feature-graphic',
    label: 'Google Play feature graphic guide',
    kind: 'guide',
    summary: 'The 1024 × 500 asset that decides whether shoppers scroll — specs and design rules.',
  },
  {
    path: '/guides/figma-app-store-screenshots',
    label: 'Figma to App Store screenshots workflow',
    kind: 'guide',
    summary: 'Taking layouts out of Figma and turning them into store-ready device mockups.',
  },
  {
    path: '/guides/how-to-make-app-store-screenshots',
    label: 'How to make App Store screenshots',
    kind: 'guide',
    summary: 'A step-by-step walkthrough from raw screen capture to a finished multi-size export.',
  },
  {
    path: '/guides/app-store-screenshot-best-practices',
    label: 'App Store screenshot best practices',
    kind: 'guide',
    summary: 'First-frame, caption, Dark Mode, iPad and A/B-testing practices, checked against Apple and Google docs.',
  },
  {
    path: '/guides/how-we-build-3d-device-mockups',
    label: 'How we build 3D device mockups (CSG)',
    kind: 'guide',
    summary: 'An engineering deep dive into the parametric CSG pipeline, with interactive demos.',
  },
  {
    path: '/guides/iphone-18-iphone-ultra-app-store-screenshots',
    label: 'iPhone 18 Pro & iPhone Duo (foldable) screenshots',
    kind: 'guide',
    summary:
      "Updated after Apple's September 2026 event — what the iPhone Duo confirmed, and what a folding screen changes about your listing.",
  },
  {
    path: '/guides/apple-september-2026-lineup-app-store-screenshots',
    label: "Apple's September 2026 lineup & your screenshots",
    kind: 'guide',
    summary:
      'iPhone 18 Pro, iPhone Duo, Apple Watch Series 12 / Ultra 4, AirPods 5 — only the Duo changes your screenshots, and its two sizes are now published.',
  },
  {
    path: '/guides/localized-app-store-screenshots',
    label: 'Localized screenshots for every device',
    kind: 'guide',
    summary: 'Ship screenshots for every language and device size by reusing one scene, not redesigning each one.',
  },
  {
    path: '/guides/screenshot-case-study-80-percent-more-downloads',
    label: 'Case study: +80% downloads in 13 weeks',
    kind: 'guide',
    summary:
      'A real App Store Connect case study on new device support and a new screenshot set, with no other marketing.',
  },
  {
    path: '/guides/free-image-converter-video-to-gif',
    label: 'New tools: image converter & video to GIF',
    kind: 'guide',
    summary: 'Two new free tools — convert between image formats, or turn a video clip into an optimized GIF.',
  },
  {
    path: '/guides/app-store-screenshot-rejection-reasons',
    label: 'App Store screenshot rejected? Fixes',
    kind: 'guide',
    summary:
      'Upload errors and the App Store screenshot guidelines behind rejections (2.3.2, 2.3.3, 2.3.10), with the fix and resubmit steps for each.',
  },
  {
    path: '/guides/app-store-header-search-results-assets',
    label: 'App Store header & search results assets',
    kind: 'guide',
    summary:
      'iOS 27 creative assets: Header 3840 × 1646, Search Results 3:2, the 5244 × 2950 Universal PNG, safe areas and content rules.',
  },
  {
    path: '/guides/app-store-icon-guidelines',
    label: 'App Store icon size & guidelines',
    kind: 'guide',
    summary:
      'Icon sizes for every Apple platform incl. classic iPhone and iPad sizes, Liquid Glass, App Review rules, icon A/B tests and the Google Play spec.',
  },
  {
    path: '/guides/google-play-screenshot-requirements',
    label: 'Google Play screenshot size and requirements',
    kind: 'guide',
    summary:
      'Google Play screenshot size (1080 × 1920 px for phones), file rules and counts for every device type including games, plus the content guidelines and Metadata policy.',
  },
  {
    path: '/guides/panoramic-app-store-screenshots',
    label: 'Panoramic App Store screenshots',
    kind: 'guide',
    summary:
      'Connected screenshot sets for the App Store and Google Play: 2026 slice sizes, canvas width with gaps, how many frames to connect, one panorama per device family and seam rules.',
  },
  {
    path: '/guides/app-store-assets',
    label: 'App Store assets checklist',
    kind: 'guide',
    summary:
      'Every App Store and Google Play listing asset with current sizes: icon, screenshots, app previews, iOS 27 creative assets, event media, feature graphic.',
  },
  {
    path: '/guides/app-store-screenshot-ab-testing',
    label: 'App Store screenshot A/B testing',
    kind: 'guide',
    summary:
      'How to A/B test store screenshots with Product Page Optimization and Play store listing experiments: limits, what to test, test length, results.',
  },
]

// Pages related to the current one (everything except itself), used for the
// in-context internal-link section that ties comparisons ↔ guides together.
export const relatedMarketingPages = (currentPath: string): readonly MarketingPage[] =>
  MARKETING_PAGES.filter((page) => page.path !== currentPath)
